// src/components/ChatScreen.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import supabaseService from '../services/supabaseService';
import { Message } from '../models';
import { FiSend, FiArrowLeft, FiX, FiCornerUpLeft, FiCheck, FiMoreVertical, FiEdit, FiCopy, FiTrash2, FiCheckSquare, FiImage, FiCheckCircle, FiVideo } from 'react-icons/fi';
import { formatTime } from '../utils/helpers';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import CallScreen from './CallScreen';

import chatBg from '../assets/chat.jpg'; 

const ChatStyles = () => (
  <style>
    {`
      * {
        -webkit-user-select: none;
        user-select: none;
      }
      input, textarea {
        -webkit-user-select: text;
        user-select: text;
      }

      html, body {
        width: 100%;
        height: 100%;
        margin: 0;
        padding: 0;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      }

      .chat-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100dvh;
  overflow: hidden;
        background-color: #f0f2f5;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
      }
      
      /* للهواتف: الصورة تغطي كامل الشاشة */
      @media (max-width: 767px) {
        .chat-container {
          background-size: cover;
        }
      }
      
      /* للحاسوب: الصورة تحافظ على أبعادها وتتوسط */
      @media (min-width: 768px) {
        .chat-container {
          background-size: contain;
          background-position: center;
          background-color: #0a0a0a;
        }
      }

      .chat-header {
        flex-shrink: 0;
        position: sticky;
        top: 0;
        z-index: 40;
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(12px);
        -webkit-app-region: drag;
      }

      .messages-container {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 180px;
        overflow-x: hidden;
        -webkit-overflow-scrolling: touch;
        scroll-behavior: smooth;
        overscroll-behavior: contain;
        position: relative;
        z-index: 10;
        min-height: 0;
      }

      .messages-container::-webkit-scrollbar {
        width: 4px;
      }

      .messages-container::-webkit-scrollbar-track {
        background: transparent;
      }

      .messages-container::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.2);
        border-radius: 2px;
      }

      .action-bar {
        flex-shrink: 0;
        position: relative;
        z-index: 20;
        background: #f4f4f5;
        border-top: 1px solid rgba(0, 0, 0, 0.1);
      }

      .input-area {
  flex-shrink: 0;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 20;
  background: #f4f4f5;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding: max(10px, env(safe-area-inset-bottom));
  padding-top: 10px;
  width: 100%;
}

      @keyframes bounce {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-4px); }
      }
      .typing-dot {
        animation: bounce 1.4s infinite ease-in-out both;
      }
      .typing-dot:nth-child(1) { animation-delay: -0.32s; }
      .typing-dot:nth-child(2) { animation-delay: -0.16s; }

      input:focus {
        outline: none;
      }

      @supports (padding: max(0px)) {
        body {
          padding-left: max(0px, env(safe-area-inset-left));
          padding-right: max(0px, env(safe-area-inset-right));
        }
      }

      .header-fixed {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 50 !important;
      }

      .selected-message {
        border: 2px solid #3b82f6 !important;
      }
    `}
  </style>
);

const ChatScreen = () => {
  const { conversationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { otherUserName, otherUserAvatarUrl } = location.state || {};
  
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isValidConversation, setIsValidConversation] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [isOtherUserTyping, setIsOtherUserTyping] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMessagesIds, setSelectedMessagesIds] = useState(new Set());

  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [showBgModal, setShowBgModal] = useState(false);
  const [currentBg, setCurrentBg] = useState(chatBg);
  const [tempBg, setTempBg] = useState(chatBg);

  // حالة مكالمة الفيديو
  const [isCallActive, setIsCallActive] = useState(false);

  // خلفيات من Supabase
  const [backgroundOptions, setBackgroundOptions] = useState([]);
  const [loadingBackgrounds, setLoadingBackgrounds] = useState(true);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const headerRef = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const pressTimer = useRef(null);

  // تحميل الخلفيات من Supabase
  useEffect(() => {
    loadBackgrounds();
  }, []);

  // تحميل تفضيلات المستخدم عند تسجيل الدخول
  useEffect(() => {
    if (currentUser) {
      loadUserPreferences();
    }
  }, [currentUser]);

  useEffect(() => {
    const handleViewportResize = () => {
      if (window.visualViewport) {
        const viewport = window.visualViewport;
        const inputArea = document.querySelector('.input-area');
        if (inputArea) {
          const offsetFromBottom = window.innerHeight - viewport.height - viewport.offsetTop;
          inputArea.style.bottom = `${offsetFromBottom}px`;
        }
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
      window.visualViewport.addEventListener('scroll', handleViewportResize);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
        window.visualViewport.removeEventListener('scroll', handleViewportResize);
      }
    };
  }, []);

  const loadBackgrounds = async () => {
    try {
      const { data, error } = await supabaseService.client
        .from('chat_backgrounds')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        setBackgroundOptions(data);
      } else {
        setBackgroundOptions([
          { id: 'default', name: 'افتراضي', type: 'image', value: chatBg }
        ]);
      }
    } catch (error) {
      console.error('Error loading backgrounds:', error);
      toast.error('فشل تحميل الخلفيات');
      setBackgroundOptions([
        { id: 'default', name: 'افتراضي', type: 'image', value: chatBg }
      ]);
    } finally {
      setLoadingBackgrounds(false);
    }
  };

  // تحديد نوع المستخدم (client أو provider)
  const getUserType = () => {
    if (!currentUser) return 'client';
    
    // طريقة 1: إذا كان لديك حقل type في currentUser
    if (currentUser.type) return currentUser.type;
    
    // طريقة 2: إذا كان لديك حقل userType في currentUser
    if (currentUser.userType) return currentUser.userType;
    
    // طريقة 3: التحقق من الجدول الذي جاء منه المستخدم
    // يمكنك إضافة منطق هنا للتحقق
    
    // افتراضياً: client
    return 'client';
  };

  // تحميل تفضيلات المستخدم من Supabase
  const loadUserPreferences = async () => {
    if (!currentUser) return;

    try {
      const userType = getUserType();

      const { data, error } = await supabaseService.client
        .from('user_preferences')
        .select('chat_background')
        .eq('user_id', currentUser.id)
        .eq('user_type', userType)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.log('Load preferences error:', error);
      }

      if (data && data.chat_background) {
        setCurrentBg(data.chat_background);
        setTempBg(data.chat_background);
      } else {
        // إذا لم توجد تفضيلات، استخدم الخلفية الافتراضية
        const defaultBg = backgroundOptions[0]?.value || chatBg;
        setCurrentBg(defaultBg);
        setTempBg(defaultBg);
      }
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  };

  // حفظ الخلفية في Supabase
  const saveUserBackground = async (backgroundValue) => {
    if (!currentUser) return;

    try {
      const userType = getUserType();

      // محاولة التحديث أولاً
      const { data: existingData, error: fetchError } = await supabaseService.client
        .from('user_preferences')
        .select('id')
        .eq('user_id', currentUser.id)
        .eq('user_type', userType)
        .single();

      if (existingData) {
        // التحديث
        const { error: updateError } = await supabaseService.client
          .from('user_preferences')
          .update({ 
            chat_background: backgroundValue,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', currentUser.id)
          .eq('user_type', userType);

        if (updateError) {
          console.error('Update error:', updateError);
          throw updateError;
        }
      } else {
        // الإدراج
        const { error: insertError } = await supabaseService.client
          .from('user_preferences')
          .insert({
            user_id: currentUser.id,
            user_type: userType,
            chat_background: backgroundValue
          });

        if (insertError) {
          console.error('Insert error:', insertError);
          throw insertError;
        }
      }

      console.log('Background saved successfully for', userType);
    } catch (error) {
      console.error('Error saving background:', error);
      toast.error('فشل حفظ الخلفية');
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.visualViewport) {
        const windowHeight = window.visualViewport.height;
        const screenHeight = window.innerHeight;
        const keyboardHeight = screenHeight - windowHeight;
        
        const isKeyboardOpen = keyboardHeight > 100;
        setKeyboardVisible(isKeyboardOpen);

        if (containerRef.current) {
          if (isKeyboardOpen) {
            containerRef.current.style.height = `${windowHeight}px`;
            containerRef.current.style.maxHeight = `${windowHeight}px`;
            setTimeout(() => {
              messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }, 100);
          } else {
            containerRef.current.style.height = '100vh';
            containerRef.current.style.maxHeight = '100vh';
          }
        }
      }
    };
    window.visualViewport?.addEventListener('resize', handleResize);
    window.addEventListener('resize', handleResize);
    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const handleFocus = () => {
      setKeyboardVisible(true);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 300);
    };
    const handleBlur = () => setKeyboardVisible(false);
    input.addEventListener('focus', handleFocus);
    input.addEventListener('blur', handleBlur);
    return () => {
      input.removeEventListener('focus', handleFocus);
      input.removeEventListener('blur', handleBlur);
    };
  }, []);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && keyboardVisible) {
          header.classList.add('header-fixed');
        } else {
          header.classList.remove('header-fixed');
        }
      },
      { threshold: 0 }
    );
    observer.observe(header);
    return () => observer.disconnect();
  }, [keyboardVisible]);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().then(permission => {
        if (permission === "granted") {
          new Notification("الإشعارات مفعلة", { body: "ستصلك إشعارات عند تلقي رسائل جديدة." });
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!conversationId) {
      setIsValidConversation(false);
      return;
    }
    const user = supabaseService.currentUser;
    if (!user) {
      setIsValidConversation(false);
      return;
    }
    setCurrentUser(user);
    setIsValidConversation(true);
    loadMessages();
    const intervalId = setInterval(() => {
      if (!isExiting) loadMessages();
    }, 1000); 
    return () => clearInterval(intervalId);
  }, [conversationId, isExiting]);

  const isUserScrollingRef = useRef(false);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceToBottom = scrollHeight - scrollTop - clientHeight;
      isUserScrollingRef.current = distanceToBottom > 100;
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceToBottom = scrollHeight - scrollTop - clientHeight;
    // فقط إذا كان في الأسفل أصلاً
    if (distanceToBottom < 50) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);
  const loadMessages = async () => {
    try {
      const conversationExists = await checkConversationExists();
      if (!conversationExists) { setIsValidConversation(false); return; }
      const messagesData = await supabaseService.getMessagesForConversation(conversationId);
      setMessages(messagesData || []);
      setIsValidConversation(true);
    } catch (error) {
      console.error("Error loading messages:", error);
    } finally {
      if (loading) setLoading(false);
    }
  };

  const checkConversationExists = async () => {
    try {
      const { data, error } = await supabaseService.client.from('conversations').select('id').eq('id', conversationId).single();
      return !error && data;
    } catch (error) { return false; }
  };

  const scrollToBottom = () => {
  const container = messagesContainerRef.current;
  if (container && !isUserScrollingRef.current) {
    container.scrollTop = container.scrollHeight;
  }
};

  const sendMessage = async (e) => {
  e.preventDefault();
  if (!newMessage.trim() || !currentUser || !isValidConversation) return;
  
  const messageContent = newMessage.trim();

  if (editingMessage) {
    try {
      const { error } = await supabaseService.client
        .from('messages')
        .update({ content: messageContent })
        .eq('id', editingMessage.id);

      if (!error) {
        setMessages(prev => prev.map(msg => 
          msg.id === editingMessage.id ? { ...msg, content: messageContent } : msg
        ));
        toast.success('تم تعديل الرسالة');
      } else {
        throw error;
      }
    } catch (error) {
      console.error("Error editing message:", error);
      toast.error('فشل تعديل الرسالة');
    } finally {
      setEditingMessage(null);
      setNewMessage('');
    }
    return;
  }

  try {
    const { data, error } = await supabaseService.sendMessage(conversationId, messageContent, { replyToId: replyingTo?.id });
    if (!error && data) {
      setMessages(prev => {
        const updated = [...prev, data];
        return updated.sort((a, b) =>
          new Date(a.createdAt || a.created_at) - new Date(b.createdAt || b.created_at)
        );
      });
      
      if (Notification.permission === "granted") {
        new Notification(`أرسلت رسالة إلى ${otherUserName}`, { 
          body: messageContent,
          icon: '/logo.png'
        });
      }
    }
    setNewMessage('');
    setReplyingTo(null);
  } catch (error) {
    console.error("Error sending message:", error);
    toast.error('فشل إرسال الرسالة');
  }
};

  const handleReply = (message) => {
    setReplyingTo(message);
    setShowOptions(false);
    setEditingMessage(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleEdit = (message) => {
    setEditingMessage(message);
    setNewMessage(message.content);
    setShowOptions(false);
    setReplyingTo(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleCopy = async (text) => {
    if (!text) {
      toast.error('لا يوجد نص للنسخ');
      return;
    }
    
    try {
      await navigator.clipboard.writeText(text);
      toast.success('تم نسخ النص');
      setShowOptions(false);
    } catch (err) {
      console.error('Failed to copy: ', err);
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('تم نسخ النص');
        setShowOptions(false);
      } catch (fallbackErr) {
        console.error('Fallback copy failed: ', fallbackErr);
        toast.error('فشل النسخ');
      }
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الرسالة؟')) return;
    
    const originalMessages = [...messages];
    setMessages(prev => prev.filter(msg => msg.id !== messageId));

    try {
      const { error } = await supabaseService.client
        .from('messages')
        .delete()
        .eq('id', messageId);

      if (error) throw error;
      
      toast.success('تم حذف الرسالة');
    } catch (error) {
      console.error("Error deleting message:", error);
      setMessages(originalMessages);
      toast.error(`فشل حذف الرسالة: ${error.message}`);
    } finally {
      setShowOptions(false);
    }
  };

  const deleteSelectedMessages = async () => {
    if (selectedMessagesIds.size === 0) return;
    if (!window.confirm(`هل أنت متأكد من حذف ${selectedMessagesIds.size} رسالة؟`)) return;

    const idsToDelete = Array.from(selectedMessagesIds);
    const originalMessages = [...messages];

    setMessages(prev => prev.filter(msg => !selectedMessagesIds.has(msg.id)));
    
    try {
      const { error } = await supabaseService.client
        .from('messages')
        .delete()
        .in('id', idsToDelete);

      if (error) throw error;
      
      toast.success('تم حذف الرسائل المحددة');
      setSelectionMode(false);
      setSelectedMessagesIds(new Set());
    } catch (error) {
      console.error("Error deleting messages:", error);
      setMessages(originalMessages);
      toast.error('فشل حذف الرسائل');
    }
  };

  const showMessageOptions = (message, e) => {
    if (e) e.stopPropagation();
    setSelectedMessage(message);
    setShowOptions(true);
  };

  const toggleSelection = (id) => {
    const newSelection = new Set(selectedMessagesIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedMessagesIds(newSelection);
  };

  const startPress = (message) => {
    pressTimer.current = setTimeout(() => {
      if (navigator.vibrate) navigator.vibrate(50);
      setSelectionMode(true);
      toggleSelection(message.id);
    }, 500);
  };

  const endPress = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
    }
  };

  const isMyMessage = (message) => currentUser && message.senderId === currentUser.id;
  
  const cancelAction = () => {
    setEditingMessage(null);
    setReplyingTo(null);
    setNewMessage('');
  };

  const selectBg = (bgValue) => {
    setTempBg(bgValue);
  };

  const confirmBg = async () => {
    setCurrentBg(tempBg);
    await saveUserBackground(tempBg); // حفظ الخلفية في Supabase
    setShowBgModal(false);
    toast.success('تم تغيير وحفظ الخلفية');
  };

  const cancelBg = () => {
    setTempBg(currentBg);
    setShowBgModal(false);
  };

  if (!isValidConversation && !loading) return <div className="flex flex-col h-screen bg-white"><div className="flex p-4 border-b"><button onClick={() => navigate('/client')} className="p-2"><FiArrowLeft /></button><h3 className="mr-3 font-medium text-red-600">المحادثة غير صالحة</h3></div><div className="flex-1 flex items-center justify-center"><h2 className="text-xl font-bold">المحادثة غير موجودة</h2></div></div>;
  if (loading) return <div className="h-screen bg-white flex items-center justify-center"><div className="animate-spin h-8 w-8 border-b-2 border-gray-900 mx-auto mb-2"></div></div>;
  if (!currentUser) return <div className="h-screen bg-white flex items-center justify-center"><h2 className="text-xl font-bold">يجب تسجيل الدخول</h2></div>;

  return (
    <div 
      ref={containerRef}
      className="chat-container bg-cover bg-center bg-no-repeat" 
      style={{ 
        background: currentBg.startsWith('linear-gradient') || currentBg.startsWith('#') 
          ? currentBg 
          : `url(${currentBg}) center/cover no-repeat`
      }}
    >
      <ChatStyles />
      <div className="absolute inset-0 bg-white/10 pointer-events-none"></div>

      {/* Header */}
      <div 
        ref={headerRef}
        className="chat-header shadow-sm px-3 py-2 flex items-center justify-between z-30 border-b border-gray-200/50 gap-3 transition-all duration-300" 
        dir="ltr"
      >
        <AnimatePresence mode="wait">
          {!selectionMode ? (
            <motion.div 
              key="normal-header"
              initial={{ opacity: 0, y: -10 }} 
              animate={{ opacity: 1, y: 0 }} 
              exit={{ opacity: 0, y: -10 }}
              className="flex items-center w-full justify-between"
            >
              <div className="flex items-center gap-3">
                <button onClick={() => navigate(-1)} disabled={isExiting} className="p-2 rounded-full hover:bg-gray-200 transition-colors text-gray-700 flex-shrink-0">
                  <FiArrowLeft />
                </button>
                <div className="relative flex-shrink-0 cursor-pointer">
                  <img src={otherUserAvatarUrl || 'https://via.placeholder.com/150'} alt="Avatar" className="w-10 h-10 rounded-full border-2 border-white shadow-sm object-cover" />
                  <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
                </div>
                <div className="flex flex-col min-w-0" dir="rtl">
                  <h3 className="font-bold text-gray-800 text-sm truncate">{otherUserName || 'مستخدم'}</h3>
                  <p className="text-[11px] text-green-600 font-medium truncate flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full inline-block"></span>
                    {isOtherUserTyping ? 'يكتب الآن...' : 'متصل الآن'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1">
                {/* زر مكالمة فيديو */}
                <button
                  onClick={async () => {
                    let otherUserId = location.state?.otherUserId;
                    if (!otherUserId) {
                      const otherMsg = messages.find(m => m.senderId && m.senderId !== currentUser?.id);
                      if (otherMsg) otherUserId = otherMsg.senderId;
                    }
                    if (!otherUserId) {
                      try {
                        const { data } = await supabaseService.client
                          .from('conversations')
                          .select('client_id, provider_id')
                          .eq('id', conversationId)
                          .single();
                        if (data) {
                          otherUserId = data.client_id === currentUser?.id ? data.provider_id : data.client_id;
                        }
                      } catch (e) {
                        console.error('Error getting recipient ID:', e);
                      }
                    }

                    if (window.__startVideoCall && otherUserId) {
                      window.__startVideoCall(conversationId, otherUserId, otherUserName || 'المستخدم');
                    } else {
                      setIsCallActive(true);
                    }
                  }}
                  className="p-2 rounded-full hover:bg-blue-50 text-blue-600 transition-colors"
                  title="مكالمة فيديو"
                >
                  <FiVideo className="w-5 h-5" />
                </button>

                <button 
                  onClick={() => setShowHeaderMenu(true)} 
                  className="p-2 rounded-full hover:bg-gray-200 transition-colors text-gray-600"
                >
                  <FiMoreVertical />
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div 
              key="selection-header"
              initial={{ opacity: 0, y: -10 }} 
              animate={{ opacity: 1, y: 0 }} 
              exit={{ opacity: 0, y: -10 }}
              className="flex items-center w-full justify-between"
            >
              <button 
                onClick={() => {
                  setSelectionMode(false);
                  setSelectedMessagesIds(new Set());
                }} 
                className="p-2 rounded-full hover:bg-gray-200 text-gray-600"
              >
                <FiX />
              </button>
              <span className="font-bold text-gray-800">{selectedMessagesIds.size} محدد</span>
              <button 
                onClick={deleteSelectedMessages}
                className="p-2 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
              >
                <FiTrash2 />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Messages Area */}
      <div 
        ref={messagesContainerRef} 
        className="messages-container p-3 space-y-2 relative" 
        dir="ltr"
      >
        <AnimatePresence>
          {messages.map((message) => {
            const mine = isMyMessage(message);
            const isSelected = selectionMode && selectedMessagesIds.has(message.id);
            
            return (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className={`flex w-full ${mine ? 'justify-end' : 'justify-start'} relative group items-end`}
                onMouseDown={() => startPress(message)}
                onMouseUp={endPress}
                onMouseLeave={endPress}
                onTouchStart={() => startPress(message)}
                onTouchEnd={endPress}
              >
                {!mine && (
                  <div className="absolute inset-y-0 left-0 flex items-center pl-1 pr-8 pointer-events-none z-0">
                    <motion.div 
                      className="bg-blue-500 rounded-full p-1.5 text-white shadow-lg"
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                    >
                      <FiCornerUpLeft className="w-4 h-4" />
                    </motion.div>
                  </div>
                )}

                {isSelected && (
                  <div className={`${mine ? 'ml-1' : 'mr-1'} flex-shrink-0`}>
                    <FiCheckSquare className="text-blue-500 w-5 h-5" />
                  </div>
                )}

                <motion.div
                  dir="rtl" 
                  onClick={(e) => {
                    if (selectionMode) {
                      e.stopPropagation();
                      if (mine) toggleSelection(message.id);
                    } else {
                      showMessageOptions(message, e);
                    }
                  }}
                  className={`relative max-w-[75%] px-3 py-1.5 shadow-sm z-10 break-words transition-all duration-200 cursor-pointer select-none
                    ${mine 
                      ? 'bg-black text-white rounded-2xl rounded-tr-sm' 
                      : 'bg-white text-gray-900 rounded-2xl rounded-tl-sm'
                    }
                    ${isSelected ? 'selected-message' : ''}
                  `}
                >
                  {message.replyToMessage && (
                    <div 
                      className={`mb-1 p-1.5 rounded-md text-[10px] border-l-2 truncate leading-tight
                        ${mine 
                          ? 'bg-gray-800/50 border-gray-600 text-gray-300' 
                          : 'bg-gray-100 border-gray-300 text-gray-500'
                        }
                      `}
                    >
                      <p className="font-bold mb-0.5 opacity-80 flex items-center gap-1 uppercase tracking-wide">
                        {isMyMessage(message.replyToMessage) ? 'أنت' : otherUserName}
                      </p>
                      <p className="font-normal">{message.replyToMessage.content}</p>
                    </div>
                  )}
                  
                  <p className="text-[15px] leading-snug font-normal whitespace-pre-wrap">
                    {message.content}
                  </p>
                  
                  <div className={`flex items-center justify-end gap-0.5 mt-0.5 select-none ${mine ? 'flex-row' : 'flex-row-reverse'}`}>
                    <p className="text-[10px] opacity-70">
                      {formatTime(message.createdAt)}
                    </p>
                    {mine && (
                      <div className="flex items-center">
                        <FiCheck className="w-3 h-3 text-[#53bdeb]" />
                      </div>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {isOtherUserTyping && (
          <div className="flex justify-start w-full" dir="ltr">
            <div className="bg-white px-3 py-1.5 rounded-2xl rounded-tl-none shadow-sm ml-auto flex items-center gap-1">
              <span className="w-1 h-1 bg-gray-400 rounded-full typing-dot"></span>
              <span className="w-1 h-1 bg-gray-400 rounded-full typing-dot"></span>
              <span className="w-1 h-1 bg-gray-400 rounded-full typing-dot"></span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Action Bar */}
      <AnimatePresence>
        {(replyingTo || editingMessage) && (
          <motion.div 
            initial={{ opacity: 0, height: 0, y: 20 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: 20 }}
            className="action-bar p-2 flex justify-between items-center"
            dir="rtl"
          >
            <div className="flex items-center gap-3 overflow-hidden flex-1">
              <div className="bg-gray-300 p-1 rounded-full text-gray-700 flex-shrink-0">
                {editingMessage ? <FiEdit className="w-3.5 h-3.5" /> : <FiArrowLeft className="transform rotate-180 w-3.5 h-3.5" />}
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-[11px] font-bold text-gray-700 uppercase tracking-wide">
                  {editingMessage ? 'تعديل الرسالة' : `الرد على ${isMyMessage(replyingTo) ? 'نفسك' : otherUserName}`}
                </span>
                <span className="text-sm text-gray-900 truncate font-medium leading-tight">
                  {editingMessage ? editingMessage.content : replyingTo.content}
                </span>
              </div>
            </div>
            <button
              onClick={cancelAction}
              className="p-1.5 hover:bg-gray-300 rounded-full text-gray-600 transition-colors flex-shrink-0"
            >
              <FiX className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input Area */}
      <form 
        onSubmit={sendMessage} 
        className="input-area flex-shrink-0" 
        dir="rtl"
      >
        <div className="flex items-end gap-2 px-2.5">
          <div className="flex-1 bg-white rounded-[22px] flex items-center px-4 py-2 shadow-sm border border-gray-200 focus-within:border-gray-400 transition-all">
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={editingMessage ? "تعديل الرسالة..." : "اكتب رسالتك..."}
              className="flex-1 bg-transparent border-none focus:outline-none text-gray-800 placeholder-gray-400 text-[15px] leading-6"
              disabled={isExiting}
              autoComplete="off"
            />
          </div>
          <button
            type="submit"
            className={`p-2 rounded-full shadow-md transform active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 ${
              editingMessage ? 'bg-[#53bdeb] hover:bg-[#4aa3d6] text-white' : 'bg-[#53bdeb] hover:bg-[#4aa3d6] text-white'
            }`}
            disabled={isExiting || !newMessage.trim()}
          >
            {editingMessage ? <FiCheck className="w-5 h-5" /> : <FiSend className="w-5 h-5 transform rotate-180" />}
          </button>
        </div>
      </form>

      {/* Header Menu (Three Dots) */}
      <AnimatePresence>
        {showHeaderMenu && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 bg-transparent z-50" 
            onClick={() => setShowHeaderMenu(false)}
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, x: 10, y: -10 }} 
              animate={{ opacity: 1, scale: 1, x: 0, y: 0 }} 
              exit={{ opacity: 0, scale: 0.9, x: 10, y: -10 }} 
              className="absolute top-14 right-3 bg-white rounded-xl shadow-2xl p-1 w-44 overflow-hidden" 
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                onClick={() => {
                  setShowHeaderMenu(false);
                  setShowBgModal(true);
                  setTempBg(currentBg);
                }}
                className="w-full text-right py-2.5 px-3 hover:bg-gray-50 text-gray-700 font-medium flex items-center gap-2 transition-colors rounded-lg text-sm"
              >
                <span className="bg-purple-50 p-1.5 rounded-full text-purple-600"><FiImage className="w-3.5 h-3.5" /></span> 
                تغيير الخلفية
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Options Modal */}
      <AnimatePresence>
        {showOptions && selectedMessage && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowOptions(false)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl p-1.5 w-full max-w-xs shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} dir="rtl">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(selectedMessage.content);
                }} 
                className="w-full text-right py-3 px-4 hover:bg-gray-50 text-gray-700 font-medium flex items-center gap-3 transition-colors rounded-xl"
              >
                <span className="bg-purple-50 p-2 rounded-full text-purple-600"><FiCopy className="w-4 h-4" /></span> نسخ النص
              </button>
              <button onClick={() => handleReply(selectedMessage)} className="w-full text-right py-3 px-4 hover:bg-gray-50 text-gray-700 font-medium flex items-center gap-3 transition-colors rounded-xl"><span className="bg-blue-50 p-2 rounded-full text-blue-600"><FiArrowLeft className="transform rotate-180" /></span> رد</button>
              {isMyMessage(selectedMessage) && (
                <>
                  <button onClick={() => handleEdit(selectedMessage)} className="w-full text-right py-3 px-4 hover:bg-gray-50 text-gray-700 flex items-center gap-3"><span className="bg-gray-100 p-2 rounded-full text-gray-600">✏️</span> تعديل</button>
                  <button onClick={() => handleDelete(selectedMessage.id)} className="w-full text-right py-3 px-4 hover:bg-red-50 text-red-600 flex items-center gap-3"><span className="bg-red-50 p-2 rounded-full text-red-600">🗑️</span> حذف</button>
                </>
              )}
              <div className="h-px bg-gray-100 my-1"></div>
              <button onClick={() => setShowOptions(false)} className="w-full text-right py-2.5 px-4 hover:bg-gray-50 text-gray-500 text-sm font-medium">إلغاء</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Background Modal (Bottom Sheet) */}
      <AnimatePresence>
        {showBgModal && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex items-end justify-center" 
            onClick={() => cancelBg()}
          >
            <motion.div 
              initial={{ y: "100%" }} 
              animate={{ y: 0 }} 
              exit={{ y: "100%" }} 
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="bg-white rounded-t-3xl w-full max-w-lg pb-6 pt-4 px-4 shadow-2xl" 
              onClick={(e) => e.stopPropagation()}
              dir="rtl"
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-gray-800">تغيير الخلفية</h2>
                <button onClick={cancelBg} className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 text-gray-600 transition-colors">
                  <FiX />
                </button>
              </div>

              {loadingBackgrounds ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-6 max-h-64 overflow-y-auto">
                    {backgroundOptions.map((bg) => (
                      <div
                        key={bg.id}
                        onClick={() => selectBg(bg.value)}
                        className={`
                          relative rounded-xl overflow-hidden cursor-pointer border-2 transition-all h-20
                          ${tempBg === bg.value ? 'border-blue-500 scale-105 shadow-lg' : 'border-transparent hover:border-gray-300'}
                        `}
                        style={{ 
                          background: bg.type === 'image' ? `url(${bg.value}) center/cover` : bg.value 
                        }}
                      >
                        {tempBg === bg.value && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                            <div className="bg-white p-1 rounded-full">
                              <FiCheckCircle className="w-5 h-5 text-blue-500" />
                            </div>
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] text-center py-0.5 px-1">
                          {bg.name}
                        </div>
                      </div>
                    ))}
                  </div>

                  <button 
                    onClick={confirmBg}
                    className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-bold shadow-lg transform active:scale-95 transition-all flex items-center justify-center gap-2"
                  >
                    <FiCheckCircle className="w-5 h-5" />
                    تطبيق وحفظ
                  </button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* شاشة مكالمة الفيديو للطرف المتصل إذا لم تكن عالمية */}
      {isCallActive && currentUser && (
        <CallScreen
          conversationId={conversationId}
          myId={currentUser.id}
          otherUserName={otherUserName || 'المستخدم'}
          isCaller={true}
          onEnd={() => setIsCallActive(false)}
        />
      )}

    </div>
  );
};

export default ChatScreen;